import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import ImageUpload from "@/components/admin/ImageUpload";

const empty = { name: "", capacity: 8, description: "", features: "", photo_path: null, visible: true, order: 0 };

export default function FleetAdmin() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/fleet").then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...empty });
  const openEdit = (row) => setEditing({ ...row, features: (row.features || []).join(", ") });

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        name: editing.name.trim(),
        capacity: Number(editing.capacity) || 1,
        description: editing.description,
        features: String(editing.features || "").split(",").map((s) => s.trim()).filter(Boolean),
        photo_path: editing.photo_path || null,
        visible: !!editing.visible,
        order: Number(editing.order) || 0,
      };
      if (editing.id) {
        const { data } = await api.patch(`/admin/fleet/${editing.id}`, body);
        setRows((rs) => (Array.isArray(rs) ? rs : []).map((r) => r.id === data.id ? data : r));
      } else {
        const { data } = await api.post("/admin/fleet", body);
        setRows((rs) => [...(Array.isArray(rs) ? rs : []), data]);
      }
      toast.success("Guardado");
      setEditing(null);
    } catch (err) { toast.error(err?.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar vehículo?")) return;
    await api.delete(`/admin/fleet/${id}`);
    setRows((rs) => (Array.isArray(rs) ? rs : []).filter((r) => r.id !== id));
    toast.success("Vehículo eliminado");
  };

  const safeRows = Array.isArray(rows) ? rows : [];

  return (
    <div data-testid="fleet-admin">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[#E8C87A] text-xs uppercase tracking-[0.35em] mb-2">Flota</div>
          <h1 className="font-display text-3xl md:text-4xl">Vehículos disponibles</h1>
        </div>
        <button onClick={openNew} data-testid="fleet-new" className="btn-gold px-4 py-2.5 rounded-full text-sm inline-flex items-center gap-2">
          <Plus size={14} /> Nuevo vehículo
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {safeRows.map((v) => (
          <div key={v.id} data-testid={`fleet-item-${v.id}`} className="card-lux overflow-hidden">
            <div className="aspect-[16/10] bg-[#171412]">
              {v.photo_path ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${v.photo_path}`} alt={v.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#F5EFE0]/30 text-xs">Sin imagen</div>
              )}
            </div>
            <div className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-display text-xl text-[#F5EFE0]">{v.name}</div>
                  <div className="text-xs text-[#F5EFE0]/60">{v.capacity} pasajeros</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${v.visible ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : "border-[#3A3229] text-[#F5EFE0]/50"}`}>
                  {v.visible ? "Visible" : "Oculto"}
                </span>
              </div>
              <p className="text-xs text-[#F5EFE0]/55 mt-2 line-clamp-2">{v.description}</p>
              <div className="flex justify-end gap-2 mt-4">
                <button onClick={() => openEdit(v)} data-testid={`fleet-edit-${v.id}`} className="btn-ghost-gold px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1"><Pencil size={12} /> Editar</button>
                <button onClick={() => del(v.id)} data-testid={`fleet-delete-${v.id}`} className="p-1.5 rounded-md border border-[#3A3229] text-[#F5EFE0]/60 hover:border-rose-500/60 hover:text-rose-400" style={{ transitionProperty: "border-color,color", transitionDuration: "200ms" }}><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
        {safeRows.length === 0 && <div className="card-lux p-10 col-span-full text-center text-[#F5EFE0]/50">Añade tu primer vehículo.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/70 px-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="fleet-modal" className="card-lux w-full max-w-lg p-6 md:p-8 bg-[#171412] max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-2xl">{editing.id ? "Editar vehículo" : "Nuevo vehículo"}</h2>
              <button onClick={() => setEditing(null)} className="p-1 text-[#F5EFE0]/60"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <ImageUpload path={editing.photo_path} onChange={(p) => setEditing((f) => ({ ...f, photo_path: p }))} />
              <div>
                <label className="text-xs text-[#F5EFE0]/60 block mb-1">Nombre</label>
                <input data-testid="fleet-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full bg-[#0A0908] border border-[#3A3229] rounded-lg px-3 py-2.5 focus:border-[#D4AF37] outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#F5EFE0]/60 block mb-1">Capacidad</label>
                  <input data-testid="fleet-capacity" type="number" min="1" value={editing.capacity} onChange={(e) => setEditing({ ...editing, capacity: e.target.value })} className="w-full bg-[#0A0908] border border-[#3A3229] rounded-lg px-3 py-2.5 focus:border-[#D4AF37] outline-none" />
                </div>
                <div>
                  <label className="text-xs text-[#F5EFE0]/60 block mb-1">Orden</label>
                  <input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} className="w-full bg-[#0A0908] border border-[#3A3229] rounded-lg px-3 py-2.5 focus:border-[#D4AF37] outline-none" />
                </div>
              </div>
              <div>
                <label className="text-xs text-[#F5EFE0]/60 block mb-1">Descripción</label>
                <textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full bg-[#0A0908] border border-[#3A3229] rounded-lg px-3 py-2.5 min-h-[80px] focus:border-[#D4AF37] outline-none resize-none" />
              </div>
              <div>
                <label className="text-xs text-[#F5EFE0]/60 block mb-1">Características (separadas por coma)</label>
                <input value={editing.features} onChange={(e) => setEditing({ ...editing, features: e.target.value })} placeholder="Wi-Fi, AC, USB" className="w-full bg-[#0A0908] border border-[#3A3229] rounded-lg px-3 py-2.5 focus:border-[#D4AF37] outline-none" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.visible} onChange={(e) => setEditing({ ...editing, visible: e.target.checked })} className="accent-[#D4AF37]" />
                Visible en el sitio público
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setEditing(null)} className="btn-ghost-gold px-4 py-2 rounded-full text-sm">Cancelar</button>
              <button onClick={save} data-testid="fleet-save" disabled={saving} className="btn-gold px-4 py-2 rounded-full text-sm">{saving ? "Guardando…" : "Guardar"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
